// src/utils/uploadToS3.js
import { api, getAuthHeaders } from './api';
import { updateUser } from '../redux/updateUserSlice';
import axios from 'axios';

// Helper to calculate SHA256 checksum
export const calculateSHA256 = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
  return btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
};

/**
 * Uploads a file to S3 via backend signed URL and updates user profile if needed.
 * @param {File} file - The file to upload
 * @param {Object} options - { user, setImageUploading, setProgresses, dispatch, setUserData, showSuccessToast, showErrorToast, setErrorMessages, getUser }
 * @returns {Promise<string>} - Resolves to the uploaded file URL
 */
export const uploadToS3 = async (file, {
  user,
  userId,
  setImageUploading = () => {},
  setProgresses = () => {},
  dispatch = () => {},
  setUserData = () => {},
  showSuccessToast = () => {},
  showErrorToast = () => {},
  setErrorMessages = () => {},
  getUser = () => {},
  updateReduxUser = true,
  
} = {}) => {
  // Prefer userId if provided, else user?.id
  const resolvedUserId = userId || (user && user.id);
  if (!resolvedUserId) {
    throw new Error('uploadToS3: userId is required (pass userId or user with id property)');
  }
  const hex = await calculateSHA256(file);
  setImageUploading(true);
  try {
    const res = await api.post('/users/add-file', {
      fileName: file.name,
      userId: resolvedUserId,
    }, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
    });

    const uploadUrl = res?.data?.url;
    const fileUrl = res?.data?.fileName; // This is the final file URL to be saved

    if (!uploadUrl || !fileUrl) throw new Error('Missing upload URL or fileName');

    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        setProgresses((prev) => ({
          ...prev,
          [file.name]: percentCompleted,
        }));
      },
    });
    if (updateReduxUser) {
      const payload = { image: fileUrl };
      await dispatch(updateUser({ id: user?.id, data: payload }));
      setUserData((prev) => ({ ...prev, image: fileUrl }));
      const token = localStorage.getItem('access_token');
      if (token && getUser) {
        await getUser(token);
      }
    }
    // showSuccessToast('Image uploaded successfully!');
    showSuccessToast('File uploaded successfully! Extracting details and filling the form...');
    return fileUrl;
  } catch (error) {
    setProgresses((prev) => ({ ...prev, [file.name]: 0 }));
    setErrorMessages((prev) => ({ ...prev, [file.name]: `Failed to upload. Please try again.` }));
    console.error('Upload error:', error);
    showErrorToast('Image upload failed!');
    throw error;
  } finally {
    setImageUploading(false);
    
  }
};
